import PropTypes from 'prop-types'
import { forwardRef, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

// material-ui
import { DataGrid } from '@mui/x-data-grid'
import { Paper, Toolbar, Stack, Box, Badge, IconButton, Divider, Menu, MenuItem } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery';

//third party
import { FilterOutlined, DashOutlined } from '@ant-design/icons';

//project imports
import Search from 'layout/MainLayout/Header/HeaderContent/Search'
import { FilteredList } from 'components/list/FilteredList'
import { EVENT_DARWER_FILTER_TOGGLE, FilterDrawer } from 'components/list/FilterDrawer'
import { useEventBusContext } from 'services/provider/EventBusProvider'
import { setFilterState, setPaginationState, setSearchState, setSortState } from 'store/reducers/listgrid'

// ==============================|| TRANSITIONS ||============================== //

const dataToParam = (data) => {
    const params = []

    for (var key in data) {
        const item = data[key]
        if (params[item.field] == undefined) {
            params[item.field] = item.value
        } else {
            const oldval = params[item.field]
            if (!Array.isArray(oldval)) {
                params[item.field] = [oldval]
            }
            params[item.field].push(item.value)
        }
    }
    return params
}


const DataGridActionMenu = ({ actions, filter, filters, eventbus, theme }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const menuitemsx = { display: 'flex', alignItems: 'center', minHeight: 'auto', p: '8px 16px', '& .anticon': { color: theme.palette.primary.main, mr: '10px' } };

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    return(
        <Box>
            <IconButton id="long-button" onClick={handleMenuClick} sx={{ color: '#000', transform:'rotate(90deg)' }} >
                <DashOutlined />
            </IconButton>
            <Menu id="long-menu" anchorEl={anchorEl} open={open} 
                onClose={handleMenuClose} 
                slotProps={{
                    paper: { style: { maxHeight: 48 * 4.5, width: '16ch' } }
                }}
            >
                {actions}
                {filter != null && <MenuItem color="inherit" sx={menuitemsx}
                    onClick={(e) => {
                        eventbus.fire(EVENT_DARWER_FILTER_TOGGLE)
                    }} >
                    <FilterOutlined /> Filter
                </MenuItem>}
            </Menu>
        </Box>
    )
}

const DataGridList = forwardRef(({ onInit, leading, actions, filterkey = '', needToolBar = true, needShadowEffect = true, filter, onFilter, search, searchHint, onPaginationModelChange, onSortModelChange, loading, rows, ...others }, ref) => {
    const theme = useTheme()
    const is_mobile = useMediaQuery('(max-width:991px)');
    const dispatch = useDispatch()
    const filterdata = useSelector((state) => state.listgrid.filterdata.find((item) => item.key == filterkey))
    const paginationdata = useSelector((state)=>state.listgrid.pagination.find((item)=>item.key==filterkey))
    const sortdata = useSelector((state) => state.listgrid.sort.find((item) => item.key == filterkey))
    const searchdata = useSelector((state) => state.listgrid.search.find((item) => item.key == filterkey))
    const [filters, setFilters] = useState([])
    const [ready, setReady] = useState(false)

    useEffect(() => {
        if(onInit == undefined || ready) {
            return
        }
        var filters = {}
        if(filterdata != undefined) {
            filters = dataToParam(filterdata.data)
        }
        onInit(paginationdata?.data, sortdata?.data, searchdata?.data, filters)
        setReady(true)
    }, [ready])

    useEffect(() => {
        if(filterkey == '') {
            return
        }
        if(filterdata != undefined) {
            setFilters(filterdata.data)
        }
    }, [filterkey, filterdata])

    const eventbus = useEventBusContext()

    const handleFilters = (data) => {
        const params = dataToParam(data)
        onFilter(params)
        if(filterkey == '') {
            setFilters(data)
        }else{
            dispatch(setFilterState({ key: filterkey, data: data }))
        }
    }

    return (
        <Paper sx={{ boxShadow: needShadowEffect ? theme.customShadows.soft : 'none' }} ref={ref}>
            {needToolBar && <Toolbar>
                <Stack spacing={1} direction="row">
                    {search != null && <Search initvalue={searchdata?.data} search={(query) => {
                        if(search != undefined) {
                            search(query)
                        }
                        if(filterkey != '') {
                            dispatch(setSearchState({ key: filterkey, data: query }))
                        }

                    }} searchHint={searchHint} />}
                    {leading}
                </Stack>
                <Box sx={{ flexGrow: 1 }}>
                </Box>
                {!is_mobile ? 
                    <Stack spacing={1} direction="row">
                        {actions}
                        {filter != null && <>
                            <Divider orientation="vertical" flexItem />
                            <IconButton color="inherit" sx={{ width: 50 }} onClick={() => eventbus.fire(EVENT_DARWER_FILTER_TOGGLE)}>
                                <Badge badgeContent={filters.length} max={9} color="success">
                                    <FilterOutlined />
                                </Badge>
                            </IconButton>
                        </>
                        }
                    </Stack> 
                : <DataGridActionMenu actions={actions} filter={filter} filters={filters} eventbus={eventbus} theme={theme} />}
            </Toolbar>}
            {filters.length > 0 && <Toolbar sx={{ borderTop: '1px solid #eeeeee', borderBottom: '1px solid #eeeeee', bgcolor: '#fafafa' }}>
                <FilteredList filters={filters} onChange={handleFilters} />
            </Toolbar>}
            <DataGrid pageSizeOptions={[2, 10, 25, 30, 50, 100]} loading={loading} rows={loading?[]:rows}  
                getRowClassName={(params) => params.indexRelativeToCurrentPage %2===1?"":"odd-row" }
                onPaginationModelChange={(pagination)=>{
                if(onPaginationModelChange!=undefined){
                    onPaginationModelChange(pagination)
                }
                if(filterkey!=''){
                    dispatch(setPaginationState({key:filterkey, data:pagination}))
                }
                }}
                onSortModelChange={(sortModel)=>{
                if(onSortModelChange!=undefined){
                    onSortModelChange(sortModel)
                }
                if(filterkey!=''){
                    dispatch(setSortState({key:filterkey, data:sortModel}))
                }
                }}
                {...others} />
            {filter != null && <FilterDrawer filter={() => filter(filters, handleFilters)} />}
        </Paper>
    )
})

DataGridList.propTypes = {
    children: PropTypes.node,
    type: PropTypes.node,
    position: PropTypes.node
}

DataGridList.defaultProps = {
    type: 'grow',
    position: 'top-left'
}

export default DataGridList
